import { Html5Qrcode } from 'html5-qrcode';

const SCAN_MAX_SIZES = [1800, 1280, 960, 720];

type BarcodeDetectorLike = {
  detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue?: string }>>;
};

function getBarcodeDetector(): BarcodeDetectorLike | null {
  const Detector = (window as unknown as {
    BarcodeDetector?: new (options?: { formats: string[] }) => BarcodeDetectorLike;
  }).BarcodeDetector;
  if (!Detector) return null;
  try {
    return new Detector({ formats: ['qr_code'] });
  } catch {
    return null;
  }
}

function loadHtmlImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('IMAGE_LOAD_FAILED'));
    image.src = url;
  });
}

async function fileToImageBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    const url = URL.createObjectURL(file);
    try {
      const image = await loadHtmlImage(url);
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('CANVAS_UNAVAILABLE');
      context.drawImage(image, 0, 0);
      return await createImageBitmap(canvas);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

async function bitmapToJpegFile(bitmap: ImageBitmap, maxSize: number): Promise<File> {
  const longest = Math.max(bitmap.width, bitmap.height) || 1;
  const scale = Math.min(1, maxSize / longest);
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('CANVAS_UNAVAILABLE');
  context.drawImage(bitmap, 0, 0, width, height);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('JPEG_CONVERT_FAILED'))),
      'image/jpeg',
      0.92,
    );
  });

  return new File([blob], 'cccd-qr.jpg', { type: 'image/jpeg' });
}

async function detectWithBarcodeDetector(bitmap: ImageBitmap): Promise<string | null> {
  const detector = getBarcodeDetector();
  if (!detector) return null;

  try {
    const codes = await detector.detect(bitmap);
    const value = codes.find((code) => code.rawValue?.trim())?.rawValue?.trim();
    return value || null;
  } catch {
    return null;
  }
}

async function scanWithHtml5Qrcode(file: File, elementId: string): Promise<string | null> {
  if (!document.getElementById(elementId)) return null;

  const scanner = new Html5Qrcode(elementId, { verbose: false });
  try {
    const decodedText = await scanner.scanFile(file, false);
    return decodedText?.trim() || null;
  } finally {
    try {
      await scanner.clear();
    } catch {
      // Ignore clear errors between retries.
    }
  }
}

export async function decodeQrFromImageFile(file: File, readerElementId: string): Promise<string> {
  const bitmap = await fileToImageBitmap(file);

  try {
    const detected = await detectWithBarcodeDetector(bitmap);
    if (detected) return detected;

    for (const maxSize of SCAN_MAX_SIZES) {
      const jpegFile = await bitmapToJpegFile(bitmap, maxSize);
      try {
        const decoded = await scanWithHtml5Qrcode(jpegFile, readerElementId);
        if (decoded) return decoded;
      } catch {
        // Try the next scale — camera photos of CCCD often fail at full size.
      }
    }
  } finally {
    bitmap.close?.();
  }

  throw new Error('QR_NOT_FOUND');
}
